// Replaces the system popup of every <select> with a dropdown in the smööts style.
// The native select stays in the form (value, change events, no-JS fallback); the button and
// listbox follow the ARIA listbox pattern. Opt out with data-native on the select.
const ENHANCED = new WeakSet();
let uid = 0;

function enhance(select) {
  if (ENHANCED.has(select) || select.multiple || select.hasAttribute('data-native')) return;
  ENHANCED.add(select);
  const id = `sm-dd-${++uid}`;
  const wrap = document.createElement('div');
  wrap.className = 'sm-dd';
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);
  select.classList.add('sm-dd__native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');

  const label = select.id ? document.querySelector(`label[for="${select.id}"]`) : select.closest('label');
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `sm-dd__button ${select.className.replace('sm-dd__native', '')}`.trim();
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', id);
  if (label) {
    label.id = label.id || `${id}-label`;
    button.setAttribute('aria-labelledby', `${label.id} ${id}-value`);
    if (select.id) label.htmlFor = '';
    label.addEventListener('click', (e) => { e.preventDefault(); button.focus(); });
  }
  const value = document.createElement('span');
  value.id = `${id}-value`;
  button.appendChild(value);

  const list = document.createElement('ul');
  list.className = 'sm-dd__list';
  list.id = id;
  list.setAttribute('role', 'listbox');
  list.tabIndex = -1;
  list.hidden = true;
  wrap.append(button, list);

  let active = 0;
  const options = () => [...list.children];
  const render = () => {
    list.innerHTML = '';
    [...select.options].forEach((o, i) => {
      const li = document.createElement('li');
      li.className = 'sm-dd__option';
      li.id = `${id}-o${i}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(o.selected));
      if (o.disabled) li.setAttribute('aria-disabled', 'true');
      li.textContent = o.textContent;
      li.addEventListener('click', () => choose(i));
      li.addEventListener('mousemove', () => setActive(i));
      list.appendChild(li);
    });
    value.textContent = select.selectedOptions[0]?.textContent || '';
  };
  const setActive = (i) => {
    const opts = options();
    active = Math.max(0, Math.min(opts.length - 1, i));
    opts.forEach((o, n) => o.classList.toggle('is-active', n === active));
    list.setAttribute('aria-activedescendant', opts[active]?.id || '');
    opts[active]?.scrollIntoView({ block: 'nearest' });
  };
  const open = () => {
    if (!list.hidden) return;
    render();
    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    setActive(select.selectedIndex);
    list.focus();
    document.addEventListener('pointerdown', outside, true);
  };
  const close = (focus = true) => {
    if (list.hidden) return;
    list.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', outside, true);
    if (focus) button.focus();
  };
  const outside = (e) => { if (!wrap.contains(e.target)) close(false); };
  const choose = (i) => {
    const o = select.options[i];
    if (!o || o.disabled) return;
    const changed = select.selectedIndex !== i;
    select.selectedIndex = i;
    render();
    close();
    if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  button.addEventListener('click', () => (list.hidden ? open() : close()));
  button.addEventListener('keydown', (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); open(); }
  });
  list.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(select.options.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') close(false);
  });
  select.addEventListener('change', render);
  render();
}

const scan = (root) => root.querySelectorAll?.('select').forEach(enhance);
scan(document);
new MutationObserver((records) => {
  for (const r of records) r.addedNodes.forEach((n) => { if (n.nodeType === 1) { if (n.matches('select')) enhance(n); else scan(n); } });
}).observe(document.documentElement, { childList: true, subtree: true });
